import type { TourStepDefinition } from '@dev_mat/floating-ui-tour';
import { registerTourPage } from '@/composables/tour/tourRegistry';
import { introStep, step } from '@/composables/tour/stepHelpers';
import { routeNames } from '@/constants/route-details.constant';
import {
  BILLING_NAV_SELECT_SELECTOR,
  BILLING_TAB_MENU_SELECTOR,
  billingWelcomeContent,
  debtorsTableContent,
  readBillingTabGroups,
  readBillingTabLabels,
} from '@/utils/billingTourCopy';

// Every anchored step goes through `step()`, so a target that is missing or
// hidden (permission gate, empty table, inactive tab) is skipped instead of
// leaving the popover floating over nothing.

const TRIGGER_TARGET = '#app-tour-trigger';

/** The row of pages under the Billing tabs (BillingLayout.vue); absent when a tab has one page. */
const BILLING_SUBNAV_SELECTOR = '#billing-subnav';

/** Shared wording for screens that start on the current session. */
const SESSION_SCOPE_NOTE =
  'The figures start with the current session. Clear the Session box to include older unpaid bills.';

function dashboardSteps(): TourStepDefinition[] {
  return [
    step({
      target: BILLING_TAB_MENU_SELECTOR,
      title: 'Welcome to Billing',
      // Read the groups and pages when the step opens: they depend on the
      // user's permissions.
      content: () => billingWelcomeContent(readBillingTabLabels(), readBillingTabGroups()),
      placement: 'bottom',
    }),
    // Phones get the Billing menu instead (the tab bar is hidden there, so
    // the step above is skipped, and this one is on a laptop).
    step({
      target: BILLING_NAV_SELECT_SELECTOR,
      title: 'Welcome to Billing',
      content: () => billingWelcomeContent(readBillingTabLabels(), readBillingTabGroups(), true),
      placement: 'bottom',
    }),
    step({
      target: BILLING_SUBNAV_SELECTOR,
      title: 'Pages in this tab',
      content:
        'Each tab holds a few related pages. Switch between them here; the page you are on is highlighted. '
        + 'A tab with only one page you can open shows no row.',
      placement: 'bottom',
    }),
    step({
      target: '#billing-kpi-cards',
      title: 'Money at a glance',
      content:
        `How much has been billed, how much has been collected, the balance still owed and how much is overdue. ${SESSION_SCOPE_NOTE}`,
      placement: 'bottom',
    }),
    step({
      target: '#billing-charts',
      title: 'Payments over time',
      content:
        'Money collected each month, and how parents paid (cash, bank transfer, card and so on).',
      placement: 'top',
    }),
    step({
      target: '#billing-top-debtors',
      title: 'Who owes the most',
      content:
        'The students with the largest balance owed. Click a name to open their Student account, or use the link at the top of this box to see everyone who owes.',
      placement: 'top',
    }),
    step({
      target: TRIGGER_TARGET,
      title: 'Need help again?',
      content:
        'Click this button any time to replay the guide for the page you are on. Most Billing pages have their own short guide.',
      placement: 'bottom',
    }),
  ];
}

function billsListSteps(): TourStepDefinition[] {
  return [
    step({
      target: '#bills-create-btn',
      title: 'Create a bill',
      content:
        'Start here to charge students. Choose who pays (one student, a whole class or a class section), add the fees, ' +
        'pick the term and due date, then publish it so parents can see it and pay. Not ready yet? Save it as a draft: ' +
        'parents can\'t see drafts.',
      placement: 'bottom',
    }),
    step({
      target: '#bills-filters',
      title: 'Find a bill',
      content:
        'Search, or filter by status, by who was billed, or by session and term. Can\'t find a bill you just made? Clear the Status filter so drafts show too.',
      placement: 'bottom',
    }),
    step({
      target: '#bills-table',
      title: 'Your bills',
      content:
        'Each row shows who was billed, the total, how much has been paid and the balance owed. The buttons at the end ' +
        'of a row open the bill, show the students on it, publish a draft, or cancel a bill that has no payments yet.',
      placement: 'top',
    }),
  ];
}

function billStudentsSteps(): TourStepDefinition[] {
  return [
    step({
      target: '#bill-students-summary',
      title: 'This bill at a glance',
      content:
        'How many students are on this bill, the total billed, how much has been collected, the balance still owed, and how much was taken off by exemptions and discounts.',
      placement: 'bottom',
    }),
    step({
      target: '#bill-students-table',
      title: 'Students on this bill',
      content:
        'One row per student: what they owe after any discount or exemption, what they have paid and their balance owed. The bar shows how close they are to paying in full.',
      placement: 'top',
    }),
    step({
      target: '#bill-students-actions',
      title: 'Help one student',
      content:
        'Use the buttons on a student\'s row to record a payment, see their past payments, ask for an exemption (their ' +
        'balance only changes once it is approved), or open their Student account.',
      placement: 'left',
    }),
  ];
}

function transactionsSteps(): TourStepDefinition[] {
  return [
    step({
      target: '#transactions-record-btn',
      title: 'Record a payment',
      content:
        'Use this when a parent pays at the bursary: cash, bank transfer, POS or cheque. Pick the student, enter the ' +
        'amount and how they paid, then choose which fees it covers. Payments made online appear here on their own.',
      placement: 'bottom',
    }),
    step({
      target: '#transactions-filters',
      title: 'Find a payment',
      content:
        'Filter by status, session or term, or search by the student\'s name or the payment reference.',
      placement: 'bottom',
    }),
    step({
      target: '#transactions-table',
      title: 'All payments',
      content:
        'Every payment received, newest first: the date, the student, the amount, how they paid and whether it went ' +
        'through. Use the buttons on a row to see the details or download the receipt. An online payment that is still ' +
        'awaiting confirmation can be checked again from its row.',
      placement: 'top',
    }),
  ];
}

function debtorsSteps(): TourStepDefinition[] {
  return [
    step({
      target: '#debtors-filters',
      title: 'Find who owes',
      content:
        'Narrow the list by session, term, class (or programme and level) or a smallest balance owed, or search by ' +
        `name. ${SESSION_SCOPE_NOTE} Use Export to download the list as Excel, CSV or PDF. Add the guardian phone ` +
        'column if you plan to call parents.',
      placement: 'bottom',
    }),
    step({
      target: '#debtors-table',
      title: 'Students who owe',
      // Mentions the row "Record payment" button only when it is on screen
      // (it needs the record-payments permission and at least one row).
      content: () => debtorsTableContent(),
      placement: 'top',
    }),
  ];
}

function itemsSteps(): TourStepDefinition[] {
  return [
    step({
      target: '#items-create-btn',
      title: 'Add a fee item',
      content:
        'Save the fees you charge often, such as Tuition, Lab fee or Uniform, with their usual amount. When you create a bill you just pick them from the list.',
      placement: 'bottom',
    }),
    step({
      target: '#items-table',
      title: 'Your fee items',
      content:
        'Each saved fee with its usual amount, its category and the bank account it pays into. Edit a fee, or switch ' +
        'it off when you stop charging it. Changing a fee here does not change bills that already exist.',
      placement: 'top',
    }),
    introStep({
      title: 'Categories',
      content:
        'The Categories tab on this page groups your fees, for example Tuition, Boarding or Exams. Categories keep reports tidy and let one discount cover a whole group of fees.',
    }),
  ];
}

function discountsSteps(): TourStepDefinition[] {
  return [
    step({
      target: '#discounts-create-btn',
      title: 'Create a discount',
      content:
        'Set up a discount once, for example "Staff child" or "Scholarship", and say what it takes off: a percentage or a fixed amount, on the whole bill or on certain fees.',
      placement: 'bottom',
    }),
    step({
      target: '#discounts-table',
      title: 'Your discounts',
      content:
        'Open a discount to choose which students get it. It is then taken off their bills automatically, term after ' +
        'term, until you remove them. To let one student off a single bill just once, use an exemption instead (under ' +
        'Payments, then Refunds & Exemptions).',
      placement: 'top',
    }),
  ];
}

function refundsSteps(): TourStepDefinition[] {
  return [
    step({
      target: '#refunds-filters',
      title: 'Refunds',
      content:
        'Refund requests and where they are: awaiting approval, approved, paid out or declined. Use Request refund to give money back on a payment.',
      placement: 'bottom',
    }),
    step({
      target: '#refunds-table',
      title: 'Refund requests',
      content:
        'Each request shows the student, the amount, the payment it comes from and why. A refund has two steps: first ' +
        'approve it, then mark it paid out once the money has gone back. Exemptions, which let a student off part or ' +
        'all of a bill, are on the Exemptions tab of this page.',
      placement: 'top',
    }),
  ];
}

function reportsSteps(): TourStepDefinition[] {
  return [
    step({
      target: '#reports-type-selector',
      title: 'Choose a report',
      content:
        'Pick what you want to see: money collected, balances owed, how parents paid, money by fee category, discounts and exemptions given, refunds, or money collected each day.',
      placement: 'bottom',
    }),
    step({
      target: '#reports-filters',
      title: 'Narrow it down',
      content:
        'Choose the session, term, class, payment method, fee category or dates. Click Generate to see the report ' +
        'here, or Export for an Excel, CSV or PDF copy. Exports appear under the Downloads icon at the top of the ' +
        'screen.',
      placement: 'bottom',
    }),
  ];
}

function formFeesSteps(): TourStepDefinition[] {
  return [
    step({
      target: '[data-testid="form-fees-intro"]',
      title: 'Form fees',
      content:
        'Fees applicants pay when they fill in one of your online forms, such as an application form. The fee is set on the form itself.',
      placement: 'bottom',
    }),
    step({
      target: '#form-fees-table',
      title: 'One row per form',
      content:
        'How much each form has billed, how much has been paid and what is still owed. Open a form to see who has paid and who has not.',
      placement: 'top',
    }),
  ];
}

function waiverCodesSteps(): TourStepDefinition[] {
  return [
    step({
      target: '[data-testid="waiver-lookup"]',
      title: 'Check a code',
      content:
        'Codes are only shown once, when they are generated. Type one here to see whether it was used, by whom and when, and which campus it belongs to.',
      placement: 'bottom',
    }),
    step({
      target: '[data-testid="waiver-stats"]',
      title: 'Codes by campus',
      content:
        'How many codes are available, used or revoked, in total and per campus. Click a campus to list its codes. '
        + 'A yellow note means someone paid although they used a waiver code: check whether to refund them.',
      placement: 'bottom',
    }),
    step({
      target: '[data-testid="waiver-filters"]',
      title: 'Find codes',
      content: 'Filter by form, campus, status or academic calendar, or show only codes whose applicant paid anyway.',
      placement: 'bottom',
    }),
    step({
      target: '[data-testid="waiver-codes-table"]',
      title: 'Every code',
      content:
        'Each code with its campus, form, batch, who generated it, and who used it and when. Tick codes to give them a '
        + 'campus, or revoke one that has not been used. Export saves the list as a spreadsheet.',
      placement: 'top',
    }),
  ];
}

function studentDirectorySteps(): TourStepDefinition[] {
  return [
    introStep({
      title: 'Students',
      content:
        `Every student's money in one list: what they were billed, what they have paid and the balance they still owe. ${SESSION_SCOPE_NOTE}`,
    }),
    step({
      target: '#student-directory-table',
      title: 'Reading the list',
      content:
        'Billed is the total charged, with any discounts and exemptions shown just under it. The credit column shows ' +
        'extra money already paid, which goes towards the student\'s next bills. Standing tells you at a glance ' +
        'whether the student owes, is fully paid, is in credit or has not been billed yet.',
      placement: 'top',
    }),
    step({
      target: '#student-directory-table',
      title: 'Open a student',
      content:
        'Use the eye button on a row for a quick summary, or the arrow to open the full Student account. That is where you record a payment and download receipts.',
      placement: 'top',
    }),
  ];
}

function studentAccountSteps(): TourStepDefinition[] {
  return [
    introStep({
      title: 'Student account',
      content:
        `Everything about one student's money: total billed, paid, balance owed, discounts, exemptions, refunds and credit balance. ${SESSION_SCOPE_NOTE}`,
    }),
    step({
      target: '[data-testid="btn-record-payment"]',
      title: 'Record a payment',
      content:
        'Use this when this student\'s parent pays at the bursary. Enter the amount and how they paid, then choose ' +
        'which fees it covers, or let the system split it across the unpaid fees. The balance owed updates straight ' +
        'away.',
      placement: 'left',
    }),
    introStep({
      title: 'Bills, payments and receipts',
      content:
        'Use the tabs below the totals. Bills lists every bill, with a button to download each one as a PDF. Payments ' +
        'lists the money received, with a receipt to download for each payment. The next tab shows every charge and ' +
        'payment in date order with the running balance.',
    }),
  ];
}

registerTourPage(routeNames.ADMIN_BILLING_DASHBOARD.name, {
  key: 'billing-dashboard',
  label: 'Billing Dashboard',
  getSteps: dashboardSteps,
});

registerTourPage(routeNames.ADMIN_BILLING_BILLS.name, {
  key: 'billing-bills',
  label: 'Bills',
  getSteps: billsListSteps,
});

registerTourPage(routeNames.ADMIN_BILLING_STUDENTS.name, {
  key: 'billing-bill-students',
  label: 'Students on this bill',
  getSteps: billStudentsSteps,
});

registerTourPage(routeNames.ADMIN_BILLING_TRANSACTIONS.name, {
  key: 'billing-transactions',
  label: 'Payments',
  getSteps: transactionsSteps,
});

registerTourPage(routeNames.ADMIN_BILLING_DEBTORS.name, {
  key: 'billing-debtors',
  label: 'Debtors',
  getSteps: debtorsSteps,
});

registerTourPage(routeNames.ADMIN_BILLING_ITEMS.name, {
  key: 'billing-items',
  label: 'Fee items',
  getSteps: itemsSteps,
});

registerTourPage(routeNames.ADMIN_BILLING_DISCOUNTS.name, {
  key: 'billing-discounts',
  label: 'Discounts',
  getSteps: discountsSteps,
});

registerTourPage(routeNames.ADMIN_BILLING_REFUNDS.name, {
  key: 'billing-refunds',
  label: 'Refunds & Exemptions',
  getSteps: refundsSteps,
});

registerTourPage(routeNames.ADMIN_BILLING_REPORTS.name, {
  key: 'billing-reports',
  label: 'Reports',
  getSteps: reportsSteps,
});

registerTourPage(routeNames.ADMIN_BILLING_FORM_FEES.name, {
  key: 'billing-form-fees',
  label: 'Form Fees',
  getSteps: formFeesSteps,
});

registerTourPage(routeNames.ADMIN_BILLING_WAIVER_CODES.name, {
  key: 'billing-waiver-codes',
  label: 'Waiver Codes',
  getSteps: waiverCodesSteps,
});

registerTourPage(routeNames.ADMIN_BILLING_STUDENT_DIRECTORY.name, {
  key: 'billing-student-directory',
  label: 'Students',
  getSteps: studentDirectorySteps,
});

registerTourPage(routeNames.ADMIN_BILLING_STUDENT_FINANCIALS.name, {
  key: 'billing-student-account',
  label: 'Student account',
  getSteps: studentAccountSteps,
});
